
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {Getter} from "vuex-class";
import SimpleDialog from "@/components/SimpleDialog.vue";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";
import { dataPicture } from "@/utils/pictureFonction";
import heic2any from "heic2any";
import { formattedTextValuesArray, removeVuetifyFileInputBug } from "@/utils/misc";

const MAX_BYTES_SIZE: number = 9437184; // 9,437,184 Bytes (B) = 9 Megabytes (MB)

@Component({
  components: {
    MylobyAutocomplete,
    SimpleDialog,
  },
})
export default class KeyEdit extends Vue {
  @Prop({})
  public originalKey: {
    id: number,
    name: string,
    description: string,
    companyId: number,
  };

  @Prop({ default: false })
  public displayEditKeyDialog: boolean;

  @Getter private currentUser: any;
  @Getter private isMobile: boolean;
  @Getter private canDoInEnvCompany: (right: string) => boolean;

  private updatedKey: any = null;
  private loadingSaveKey: boolean = false;
  private validForm: boolean = false;
  private preventBigPictureDialog: boolean = false;
  // Picture
  private avatar: any = null;
  private heicFile: any = null;
  private uploadedImageSrc: any = null;
  private timestampKeyImage: any = Date.now();
  private s3Src: string | null = null;
  private removeVuetifyFileInputBug: (document: any) => void = removeVuetifyFileInputBug;
  // Categories
  private searchCategory: string = "";
  private categories: any[] = [];
  private isSearchingCategories: boolean = false;
  // Tags
  private tags: any = [];
  private searchTags: string = null;
  // Building
  private displayBuildingSelection: boolean = true;

  get lang(): string {
    return this.$root.$i18n.locale;
  }

  get hasCategory(): boolean {
    return !!this.updatedKey.categoryId;
  }

  get formattedCategoriesValues(): Array<{ text: string, value: number }> {
    return formattedTextValuesArray(this.categories, this.lang);
  }

  private async mounted() {
    this.updatedKey = {...this.originalKey};
    this.getCategories();
    this.getTags();
  }

  private beforeUpdate(): void {
    this.removeVuetifyFileInputBug(document);
  }

  private checkBigPicture() {
    if (!this.avatar || this.avatar.size <= MAX_BYTES_SIZE) {
      this.saveKey();
    } else {
      this.preventBigPictureDialog = true;
    }
  }

  private async fileChange(): Promise<void> {
    this.loadingSaveKey = true;
    this.heicFile = null;
    if (this.avatar) {
      if (this.avatar.type === "image/heif" || this.avatar.type === "image/heic") {
        const blob = this.avatar as Blob;
        this.heicFile = await heic2any({blob});
        const fr = new FileReader();
        fr.readAsDataURL(this.heicFile as Blob);
        fr.addEventListener("load", () => {
          this.uploadedImageSrc = fr.result;
        });
      } else {
        const fr = new FileReader();
        fr.readAsDataURL(this.avatar);
        fr.addEventListener("load", () => {
          this.uploadedImageSrc = fr.result;
        });
      }
    } else {
      this.uploadedImageSrc = null;
    }
    this.loadingSaveKey = false;
  }

  private async savePicture(imgInfo: any, profile): Promise<void> {
    const data = await dataPicture(
      this.updatedKey,
      imgInfo,
      profile,
      this.currentUser,
      this.heicFile,
      this.avatar,
    );
    const saveImageRes = await this.$store.dispatch("files/uploadKeysPicture",
      {formData: data.formData, keyId : this.updatedKey.id},
    );
    data.picture.name = saveImageRes.name;
    data.picture.picturePath = data.picture.picturePathFn(saveImageRes.name);
    await this.$store.dispatch("keys/addPicture", {
      picture: data.picture,
      keyId: this.updatedKey.id,
    }).then((picture) => {
      this.updatedKey.picturePath = picture.picturePath;
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    });
  }

  private async saveKey(): Promise<void> {
    this.loadingSaveKey = true;
    if (this.uploadedImageSrc) {
      const name = "key-picture" + Date.now();
      const profile = true;
      await this.savePicture(name, profile);
    }
    return this.$store.dispatch("keys/updateById", {
      id: this.originalKey.id,
      data: {
        ...this.updatedKey,
        Tags: this.updatedKey.newTags.map((t) => t?.id),
      },
    })
      .then(() => {
        this.$emit("reset");
        this.timestampKeyImage = Date.now();
        this.$store.commit("alerts/displaySuccess", {
          icon: "icon-picto_cle-partagees",
          msg: this.$i18n?.t("alerts.key.editionSuccess"),
        });
      })
      .catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: this.$t(`alerts.error.${err?.response?.data?.error || "default"}`),
        });
      })
      .finally(() => {
        this.loadingSaveKey = false;
      });
  }

  private getTags(): void {
    this.$store.dispatch("tags/getAll", {
      query: {
        search: this.searchTags,
        lang: this.lang,
      },
    })
      .then((res) => {
        this.tags = res;
      });
  }

  private getCategories(): void {
    this.isSearchingCategories = true;
    this.$store.dispatch("categories/getAll")
      .then((res) => {
        this.categories = res.categories;
        this.isSearchingCategories = false;
      });
  }

  private removeTags(item): void {
    const index = this.updatedKey.newTags.findIndex((i) => i.id === item.id);
    if (index >= 0) {
      this.updatedKey.newTags.splice(index, 1);
    }
  }

  private removeTagDuplicates(completeList, duplicates): any[] | null {
    if (!completeList || !duplicates) {
      return null;
    }
    return completeList.filter((item: any) =>
      !duplicates.map((elem: any) => elem?.id)?.includes(item?.id));
  }

  private close() {
    this.$emit("close");
  }

  private hasRight(right: string): boolean {
    return this.canDoInEnvCompany(right);
  }
}
